import React from 'react';
import { Flag, Icon, Chip, Button, TeamCard } from './primitives.jsx';
import { ShareModal } from './modals.jsx';

// ============================================================================
// Sub-view: Leaderboard (pool + global, Super 6 totals)
// ============================================================================

const Leaderboard = ({ pool }) => {
  const [poolBoard, setPoolBoard] = React.useState(null);
  const [globalBoard, setGlobalBoard] = React.useState(null);

  React.useEffect(() => {
    Promise.all([
      fetch(`/api/pool/${pool.id}/leaderboard`).then(r => r.json()),
      fetch('/api/global-leaderboard').then(r => r.json()),
    ]).then(([p, g]) => {
      setPoolBoard(p.leaderboard || []);
      setGlobalBoard(g.leaderboard || []);
    }).catch(() => {});
  }, [pool.id]);

  const Table = ({ rows, emptyText }) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
      <thead>
        <tr style={{ borderBottom: '1.5px solid var(--ink)', fontFamily: 'var(--font-display)', fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-soft)' }}>
          <th style={{ padding: '8px 4px', width: 48, fontWeight: 400 }}>#</th>
          <th style={{ padding: '8px 4px', fontWeight: 400 }}>NAME</th>
          <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 400 }}>POINTS</th>
        </tr>
      </thead>
      <tbody>
        {!rows ? (
          <tr><td colSpan={3} style={{ padding: 16, fontFamily: 'var(--font-mono)', color: 'var(--ink-soft)', fontSize: 13 }}>Loading…</td></tr>
        ) : rows.length === 0 ? (
          <tr><td colSpan={3} style={{ padding: 16, fontFamily: 'var(--font-mono)', color: 'var(--ink-soft)', fontSize: 13 }}>{emptyText}</td></tr>
        ) : rows.map((row, idx) => (
          <tr key={`${row.handle}-${idx}`} style={{ borderBottom: '1px solid var(--hairline)' }}>
            <td style={{ padding: '10px 4px', fontFamily: 'var(--font-mono)', color: 'var(--ink-soft)' }}>{idx + 1}</td>
            <td style={{ padding: '10px 4px', fontFamily: 'var(--font-display)', fontSize: 15, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
              {row.member_name}
            </td>
            <td style={{ padding: '10px 4px', textAlign: 'right', fontFamily: 'var(--font-display)', fontSize: 18, color: 'var(--blue)' }}>
              {row.total}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div style={{ display: 'grid', gap: 24, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
      <section style={{ background: 'var(--paper)', border: '1.5px solid var(--hairline-strong)', borderRadius: 12, padding: 20 }}>
        <h3 style={{ margin: '0 0 12px', fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--blue)' }}>POOL · {pool.id}</h3>
        <Table rows={poolBoard} emptyText="No predictions scored yet — come back after the first match completes." />
      </section>
      <section style={{ background: 'var(--paper-warm)', border: '1.5px solid var(--hairline-strong)', borderRadius: 12, padding: 20 }}>
        <h3 style={{ margin: '0 0 12px', fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--ink)' }}>GLOBAL · TOP 100</h3>
        <Table rows={globalBoard} emptyText="Be the first on the global board." />
      </section>
    </div>
  );
};

// ============================================================================
// Sub-views: Group Standings & Matches
// ============================================================================

const GroupStandings = ({ pool, myTeam }) => {
  const computed = React.useMemo(() => {
    const groups = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];
    const stats = {};

    pool.teams.forEach(t => {
      stats[t.id] = {
        id: t.id,
        country: t.country,
        group: t.group,
        assigned_to: t.assigned_to,
        played: 0, won: 0, drawn: 0, lost: 0, gf: 0, ga: 0, gd: 0, pts: 0
      };
    });

    pool.matches.forEach(m => {
      if (m.type === 'group' && m.status === 'completed') {
        const tA = stats[m.teamA_id];
        const tB = stats[m.teamB_id];
        if (!tA || !tB) return;

        tA.played += 1;
        tB.played += 1;
        tA.gf += m.scoreA;
        tA.ga += m.scoreB;
        tB.gf += m.scoreB;
        tB.ga += m.scoreA;
        tA.gd = tA.gf - tA.ga;
        tB.gd = tB.gf - tB.ga;

        if (m.scoreA > m.scoreB) {
          tA.won += 1;
          tB.lost += 1;
          tA.pts += 3;
        } else if (m.scoreB > m.scoreA) {
          tB.won += 1;
          tA.lost += 1;
          tB.pts += 3;
        } else {
          tA.drawn += 1;
          tB.drawn += 1;
          tA.pts += 1;
          tB.pts += 1;
        }
      }
    });

    const result = {};
    groups.forEach(g => {
      const list = Object.values(stats).filter(s => s.group === g);
      list.sort((a, b) => {
        if (b.pts !== a.pts) return b.pts - a.pts;
        if (b.gd !== a.gd) return b.gd - a.gd;
        if (b.gf !== a.gf) return b.gf - a.gf;
        return a.country.localeCompare(b.country);
      });
      result[g] = list;
    });

    return result;
  }, [pool]);

  return (
    <div style={{
      display: 'grid', gap: 32,
      // 260px floor leaves room on iPhone SE (327px content width) without
      // horizontal scroll; still gives 4-up layout on desktop.
      gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    }}>
      {Object.keys(computed).map(groupName => (
        <div
          key={groupName}
          style={{
            background: 'var(--paper)',
            border: '1.5px solid var(--hairline-strong)',
            borderRadius: 12,
            padding: 20,
            boxShadow: '0 4px 12px rgba(16,21,79,0.02)'
          }}
        >
          <h3 style={{
            fontFamily: 'var(--font-display)', fontSize: 22,
            letterSpacing: '0.04em', color: 'var(--blue)',
            marginBottom: 16, marginTop: 0
          }}>GROUP {groupName}</h3>

          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--hairline)', fontFamily: 'var(--font-display)', fontSize: 13, color: 'var(--ink-soft)' }}>
                <th style={{ padding: '6px 0', fontWeight: 400 }}>TEAM</th>
                <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 400 }}>P</th>
                <th style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 400 }}>GD</th>
                <th style={{ padding: '6px 0', textAlign: 'right', fontWeight: 400 }}>PTS</th>
              </tr>
            </thead>
            <tbody>
              {computed[groupName].map(team => {
                const isUserTeam = myTeam && team.id === myTeam.id;
                return (
                  <tr
                    key={team.id}
                    style={{
                      borderBottom: '1px solid rgba(16,21,79,0.05)',
                      fontFamily: 'var(--font-body)',
                      fontSize: 15,
                      fontWeight: isUserTeam ? '700' : '400',
                      color: isUserTeam ? 'var(--blue)' : 'var(--ink)'
                    }}
                  >
                    <td style={{ padding: '10px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Flag teamId={team.id} size={22} radius={2} />
                      <span style={{ textTransform: 'uppercase', fontFamily: 'var(--font-display)', fontSize: 16, letterSpacing: '0.02em' }}>
                        {team.country}
                      </span>
                      {team.assigned_to && (
                        <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--ink-mute)', textTransform: 'uppercase' }}>
                          ({team.assigned_to})
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 8px', textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 14 }}>{team.played}</td>
                    <td style={{ padding: '10px 8px', textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 14, color: team.gd > 0 ? 'var(--success)' : team.gd < 0 ? 'var(--danger)' : 'inherit' }}>
                      {team.gd > 0 ? `+${team.gd}` : team.gd}
                    </td>
                    <td style={{ padding: '10px 0', textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 14 }}>{team.pts}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

const GroupMatches = ({ pool, onEditMatch, isAdmin }) => {
  const [filterGroup, setFilterGroup] = React.useState('ALL');

  const groups = ['ALL', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];
  const filtered = React.useMemo(() => {
    if (filterGroup === 'ALL') return pool.matches;
    return pool.matches.filter(m => {
      const team = pool.teams.find(t => t.id === m.teamA_id);
      return team && team.group === filterGroup;
    });
  }, [pool.matches, filterGroup]);

  return (
    <div>
      <div style={{
        display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 28,
        paddingBottom: 16, borderBottom: '1px solid var(--hairline)'
      }}>
        {groups.map(g => (
          <button
            key={g}
            onClick={() => setFilterGroup(g)}
            style={{
              padding: '6px 12px', borderRadius: 6, cursor: 'pointer',
              fontFamily: 'var(--font-display)', fontSize: 12, letterSpacing: '0.06em',
              background: filterGroup === g ? 'var(--ink)' : 'var(--paper-warm)',
              color: filterGroup === g ? 'var(--paper)' : 'var(--ink-soft)',
              border: 'none',
              transition: 'all 120ms'
            }}
          >
            {g === 'ALL' ? 'ALL GROUPS' : `GROUP ${g}`}
          </button>
        ))}
      </div>

      <div style={{
        display: 'grid', gap: 16,
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
      }}>
        {filtered.map(match => {
          const tA = pool.teams.find(t => t.id === match.teamA_id);
          const tB = pool.teams.find(t => t.id === match.teamB_id);
          if (!tA || !tB) return null;

          const isCompleted = match.status === 'completed';

          return (
            <div
              key={match.id}
              onClick={() => isAdmin && onEditMatch(match)}
              style={{
                background: 'var(--paper)',
                border: '1.5px solid var(--hairline-strong)',
                borderRadius: 8,
                padding: '16px 20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: isAdmin ? 'pointer' : 'default',
                transition: 'all 150ms',
                boxShadow: isAdmin ? 'hover border' : 'none',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                if (isAdmin) {
                  e.currentTarget.style.borderColor = 'var(--blue)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                if (isAdmin) {
                  e.currentTarget.style.borderColor = 'var(--hairline-strong)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Flag teamId={tA.id} size={24} radius={2} />
                  <span style={{
                    fontFamily: 'var(--font-display)', fontSize: 16, letterSpacing: '0.02em',
                    textTransform: 'uppercase', color: 'var(--ink)'
                  }}>{tA.country}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Flag teamId={tB.id} size={24} radius={2} />
                  <span style={{
                    fontFamily: 'var(--font-display)', fontSize: 16, letterSpacing: '0.02em',
                    textTransform: 'uppercase', color: 'var(--ink)'
                  }}>{tB.country}</span>
                </div>
              </div>

              <div style={{ textAlign: 'right', minWidth: 60, paddingLeft: 12 }}>
                {isCompleted ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 16 }}>
                    <div>{match.scoreA}</div>
                    <div>{match.scoreB}</div>
                  </div>
                ) : (
                  <div style={{ color: 'var(--ink-mute)', fontFamily: 'var(--font-display)', fontSize: 13 }}>
                    VS
                  </div>
                )}
              </div>

              {isAdmin && (
                <div style={{
                  position: 'absolute', top: 4, right: 4, opacity: 0.3, fontSize: 10
                }}>
                  <Icon name="edit" size={10} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// RoundsBanner — surfaces the multi-round draft state at the top of the
// dashboard. Hidden detail for single-round pools (just shows enrollment) so
// the classic one-and-done sweepstake UX is unchanged.
// ============================================================================

const RoundsBanner = ({ pool }) => {
  const norm = (s) => (s || '').trim().toLowerCase();
  const numPlayers = pool.num_players ?? 1;
  const rounds = pool.rounds ?? 1;
  const currentRound = pool.current_round ?? 1;
  const draftComplete = currentRound > rounds;

  // Distinct enrolled players (case-insensitive) preserving display casing.
  const seen = new Map();
  for (const t of pool.teams) {
    if (t.assigned_to && !seen.has(norm(t.assigned_to))) {
      seen.set(norm(t.assigned_to), t.assigned_to);
    }
  }
  const enrolledPlayers = Array.from(seen.values());

  // Who's already drawn for the CURRENT round (only meaningful while active)
  const drewThisRound = new Set(
    pool.teams
      .filter(t => t.assigned_to && t.round === currentRound)
      .map(t => norm(t.assigned_to))
  );
  const pickedThisRoundCount = drewThisRound.size;

  // Three states: 1-round pool (no round language), active draft, complete draft
  const isMultiRound = rounds > 1;

  return (
    <div style={{
      background: 'var(--paper-warm)',
      border: '1.5px solid var(--hairline-strong)',
      borderRadius: 12,
      padding: '20px 24px',
      marginBottom: 32,
      display: 'flex', flexDirection: 'column', gap: 16,
    }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        gap: 16, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          {isMultiRound && !draftComplete && (
            <span style={{
              fontFamily: 'var(--font-display)', fontSize: 28,
              letterSpacing: '0.04em', textTransform: 'uppercase',
              color: 'var(--blue)', lineHeight: 1,
            }}>
              ROUND {currentRound} <span style={{ color: 'var(--ink-mute)' }}>OF {rounds}</span>
            </span>
          )}
          {isMultiRound && draftComplete && (
            <span style={{
              fontFamily: 'var(--font-display)', fontSize: 28,
              letterSpacing: '0.04em', textTransform: 'uppercase',
              color: 'var(--gold)', lineHeight: 1,
            }}>
              DRAFT COMPLETE
            </span>
          )}
          {!isMultiRound && (
            <span style={{
              fontFamily: 'var(--font-display)', fontSize: 22,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              color: 'var(--ink)', lineHeight: 1,
            }}>
              ENROLLMENT
            </span>
          )}
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 13,
            color: 'var(--ink-soft)', letterSpacing: '0.06em',
          }}>
            {isMultiRound && !draftComplete
              ? `${pickedThisRoundCount}/${numPlayers} PICKED THIS ROUND`
              : `${enrolledPlayers.length}/${numPlayers} PLAYERS JOINED`}
          </span>
        </div>

        {!draftComplete && (
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 12,
            color: 'var(--ink-mute)', letterSpacing: '0.06em',
          }}>
            {numPlayers * rounds} TEAMS · {rounds} ROUND{rounds === 1 ? '' : 'S'}
          </span>
        )}
      </div>

      {/* Per-round dot grid: one row per round, each dot a player slot.
          Filled = drawn, empty = waiting. Only show for multi-round pools. */}
      {isMultiRound && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {Array.from({ length: rounds }, (_, i) => i + 1).map(r => {
            const drewThisR = new Set(
              pool.teams.filter(t => t.assigned_to && t.round === r).map(t => norm(t.assigned_to))
            );
            const status = r < currentRound
              ? 'done'
              : r === currentRound && !draftComplete
                ? 'active'
                : 'locked';
            return (
              <div key={r} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                opacity: status === 'locked' ? 0.4 : 1,
              }}>
                <span style={{
                  fontFamily: 'var(--font-mono)', fontSize: 11,
                  color: 'var(--ink-soft)', letterSpacing: '0.06em',
                  minWidth: 28,
                }}>R{r}</span>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {Array.from({ length: numPlayers }, (_, j) => {
                    const filled = drewThisR.size > j;
                    return (
                      <span key={j} style={{
                        width: 16, height: 16, borderRadius: 4,
                        background: filled
                          ? (status === 'done' ? 'var(--ink-mute)' : 'var(--blue)')
                          : 'transparent',
                        border: filled ? 'none' : '1.5px solid var(--hairline-strong)',
                      }} />
                    );
                  })}
                </div>
                {status === 'active' && (
                  <span style={{
                    fontFamily: 'var(--font-mono)', fontSize: 11,
                    color: 'var(--blue)', letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                  }}>
                    Open
                  </span>
                )}
                {status === 'done' && (
                  <span style={{
                    fontFamily: 'var(--font-mono)', fontSize: 11,
                    color: 'var(--ink-mute)', letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                  }}>
                    Complete
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Player roster — picked vs waiting */}
      {enrolledPlayers.length > 0 && (
        <div style={{
          display: 'flex', gap: 6, flexWrap: 'wrap',
          paddingTop: 8, borderTop: '1px solid var(--hairline)',
        }}>
          {enrolledPlayers.map(player => {
            const picked = drewThisRound.has(norm(player));
            return (
              <span key={player} style={{
                padding: '4px 10px', borderRadius: 999,
                fontFamily: 'var(--font-mono)', fontSize: 11,
                letterSpacing: '0.04em',
                background: picked ? 'var(--blue-pale)' : 'transparent',
                color: picked ? 'var(--blue)' : 'var(--ink-mute)',
                border: `1px solid ${picked ? 'var(--blue)' : 'var(--hairline-strong)'}`,
              }}>
                {picked && '✓ '}{player}
              </span>
            );
          })}
          {/* Show empty slots for unfilled player capacity */}
          {enrolledPlayers.length < numPlayers && Array.from({ length: numPlayers - enrolledPlayers.length }, (_, i) => (
            <span key={`empty-${i}`} style={{
              padding: '4px 10px', borderRadius: 999,
              fontFamily: 'var(--font-mono)', fontSize: 11,
              letterSpacing: '0.04em',
              color: 'var(--ink-faint)', border: '1px dashed var(--hairline-strong)',
            }}>
              open slot
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// GeneratorCard — admin-only invite message generator with copy button.
// ============================================================================

const GeneratorCard = ({ pool }) => {
  const [copied, setCopied] = React.useState(false);
  const poolUrl = window.location.origin + '/pool/' + pool.id;
  const message = `Join my SWEEP:WC26 pool "${pool.name}" - pick a random World Cup team and ride it to the trophy!\n\nDraw your team here: ${poolUrl}\n\nThe app handles everything - draws, scoring, leaderboards. All you need to do is show up.`;

  const handleCopy = () => {
    navigator.clipboard.writeText(message);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{
      background: 'var(--paper-warm)',
      border: '1.5px solid var(--hairline-strong)',
      borderRadius: 12,
      padding: '20px 24px',
      marginBottom: 32,
    }}>
      <h3 style={{
        fontFamily: 'var(--font-display)',
        fontSize: 18,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        color: 'var(--ink)',
        margin: '0 0 12px',
      }}>INVITE YOUR OFFICE</h3>
      <textarea
        readOnly
        value={message}
        style={{
          width: '100%',
          minHeight: 100,
          padding: 12,
          borderRadius: 8,
          border: '1.5px solid var(--hairline)',
          background: 'var(--paper)',
          fontFamily: 'var(--font-body)',
          fontSize: 14,
          color: 'var(--ink)',
          resize: 'vertical',
          lineHeight: 1.5,
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
        <Button variant="primary" onClick={handleCopy}>
          {copied ? 'COPIED!' : 'COPY'}
        </Button>
        {copied && (
          <span style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 12,
            color: 'var(--blue)',
            letterSpacing: '0.06em',
          }}>Copied to clipboard</span>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// DashboardTabBar — horizontal tabs on desktop, dropdown on narrow phones.
//
// The 5 tab labels total ~730px in a single row at the base font size, which
// blows past any portrait-phone viewport. Below ~720px we collapse to a
// labelled <select> so users can still reach every view without horizontal
// scroll or clipping.
// ============================================================================
const DASHBOARD_TABS = [
  ['teams', 'TEAMS GRID'],
  ['leaderboard', 'LEADERBOARD'],
  ['standings', 'GROUP STANDINGS'],
  ['matches', 'MATCH SCHEDULE'],
];

const DashboardTabBar = ({ tab, setTab }) => {
  const [vw, setVw] = React.useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  React.useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (vw < 720) {
    return (
      <div style={{
        marginBottom: 32, paddingBottom: 16,
        borderBottom: '2px solid var(--ink)',
      }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{
            fontFamily: 'var(--font-display)', fontSize: 12,
            letterSpacing: '0.14em', textTransform: 'uppercase',
            color: 'var(--ink-soft)', flexShrink: 0,
          }}>VIEW</span>
          <select
            value={tab}
            onChange={(e) => setTab(e.target.value)}
            style={{
              flex: 1, minWidth: 0,
              padding: '12px 14px', borderRadius: 8,
              border: '1.5px solid var(--hairline-strong)',
              background: 'var(--paper)', color: 'var(--blue)',
              fontFamily: 'var(--font-display)', fontSize: 16,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            {DASHBOARD_TABS.map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </label>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', gap: 24, borderBottom: '2px solid var(--ink)',
      marginBottom: 32, paddingBottom: 0,
      overflowX: 'auto',
    }}>
      {DASHBOARD_TABS.map(([key, label]) => (
        <button
          key={key}
          onClick={() => setTab(key)}
          style={{
            padding: '12px 4px',
            background: 'transparent',
            border: 'none',
            borderBottom: tab === key ? '4px solid var(--blue)' : '4px solid transparent',
            fontFamily: 'var(--font-display)',
            fontSize: 16,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: tab === key ? 'var(--blue)' : 'var(--ink-soft)',
            cursor: 'pointer',
            marginBottom: -3,
            whiteSpace: 'nowrap',
            transition: 'all 150ms'
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

// ============================================================================
// Screen: Dashboard
// ============================================================================

export const Dashboard = ({ pool, assignments, myTeam, onPickTeam, onGoBracket, onRefresh, isAdmin, adminSecret, onOpenReroll, onStartTour }) => {
  const [tab, setTab] = React.useState('teams');
  const [sort, setSort] = React.useState('group');
  const [filter, setFilter] = React.useState('all');
  const [shareOpen, setShareOpen] = React.useState(false);

  const sorted = React.useMemo(() => {
    let list = pool.teams.slice();
    if (filter === 'mine')        list = list.filter(t =>
      t.assigned_to && myTeam?.assigned_to &&
      t.assigned_to.toLowerCase() === myTeam.assigned_to.toLowerCase()
    );
    if (filter === 'unassigned')  list = list.filter(t => !t.assigned_to);

    if (sort === 'strength') list.sort((a, b) => b.strength - a.strength);
    else if (sort === 'name') list.sort((a, b) => a.country.localeCompare(b.country));
    else list.sort((a, b) => a.group.localeCompare(b.group) || b.strength - a.strength);
    return list;
  }, [sort, filter, pool.teams, myTeam]);

  const assignedCount = pool.teams.filter(t => t.assigned_to).length;
  const pct = (assignedCount / 48) * 100;

  const renderTabContent = (onEditMatch) => {
    switch (tab) {
      case 'leaderboard':
        return <Leaderboard pool={pool} />;
      case 'standings':
        return <GroupStandings pool={pool} myTeam={myTeam} />;
      case 'matches':
        return <GroupMatches pool={pool} onEditMatch={onEditMatch} isAdmin={isAdmin} />;
      case 'teams':
      default:
        return (
          <>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              flexWrap: 'wrap', gap: 16, marginBottom: 24,
              paddingBottom: 16, borderBottom: '1px solid var(--hairline)',
            }}>
              <div style={{ display: 'flex', gap: 4 }}>
                {[['all','ALL'], ['mine','MY TEAM'], ['unassigned','OPEN']].map(([k, l]) => (
                  <button key={k} onClick={() => setFilter(k)} style={{
                    padding: '8px 16px', borderRadius: 999, cursor: 'pointer',
                    fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    background: filter === k ? 'var(--ink)' : 'transparent',
                    color: filter === k ? 'var(--paper)' : 'var(--ink)',
                    border: 'none',
                  }}>{l}</button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <span style={{ fontFamily: 'var(--font-display)', fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginRight: 8 }}>
                  SORT BY
                </span>
                {[['group','GROUP'], ['strength','STRENGTH'], ['name','A–Z']].map(([k, l]) => (
                  <button key={k} onClick={() => setSort(k)} style={{
                    padding: '8px 14px', borderRadius: 999, cursor: 'pointer',
                    fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    background: sort === k ? 'var(--blue-pale)' : 'transparent',
                    color: sort === k ? 'var(--blue-deep)' : 'var(--ink-soft)',
                    border: 'none',
                  }}>{l}</button>
                ))}
              </div>
            </div>

            <div style={{
              display: 'grid', gap: 12,
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            }}>
              {sorted.map(team => (
                <TeamCard
                  key={team.id}
                  team={team}
                  assignedTo={team.assigned_to}
                  highlighted={myTeam && team.id === myTeam.id}
                  onClick={() => {
                    if (isAdmin) {
                      onOpenReroll(team);
                    }
                  }}
                />
              ))}
            </div>
          </>
        );
    }
  };

  return (
    <section style={{
      padding: '120px 32px 140px', maxWidth: 1440, margin: '0 auto',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
        flexWrap: 'wrap', gap: 24, marginBottom: 40,
      }}>
        <div style={{ minWidth: 0, flex: '1 1 auto' }}>
          <span style={{
            fontFamily: 'var(--font-display)', fontSize: 14, letterSpacing: '0.18em',
            textTransform: 'uppercase', color: 'var(--blue)',
          }}>POOL · {pool.id.toUpperCase()} · GROUP STAGE</span>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(40px, 5vw, 72px)',
            letterSpacing: '-0.01em', textTransform: 'uppercase', lineHeight: 0.95,
            margin: '12px 0 0',
            textWrap: 'balance',
          }}>{pool.name.toUpperCase()}</h1>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip tone="ink">{assignedCount}/48 ASSIGNED</Chip>
          {myTeam && (
            <Chip tone="blue">
              YOUR TEAM · {myTeam.country.toUpperCase()}
              {(pool.rounds ?? 1) > 1 && myTeam.round ? ` · R${myTeam.round}` : ''}
            </Chip>
          )}
          <Button variant="secondary" onClick={() => setShareOpen(true)} style={{ whiteSpace: 'nowrap' }} aria-label="Share pool">
            SHARE
          </Button>
          {onStartTour && (
            <Button variant="secondary" onClick={onStartTour} style={{ whiteSpace: 'nowrap' }}>
              TAKE THE TOUR
            </Button>
          )}
          <Button variant="secondary" onClick={onGoBracket} style={{ whiteSpace: 'nowrap' }}>
            VIEW BRACKET <Icon name="arrow-right" size={14} />
          </Button>
        </div>
      </div>

      {shareOpen && <ShareModal pool={pool} onClose={() => setShareOpen(false)} />}

      {isAdmin && <GeneratorCard pool={pool} />}

      <RoundsBanner pool={pool} />

      <div style={{
        height: 6, background: 'var(--ink-faint)', borderRadius: 999,
        overflow: 'hidden', marginBottom: 40,
      }}>
        <div style={{
          height: '100%', width: `${pct}%`,
          background: 'var(--blue)',
          transition: 'width 480ms var(--ease-out)',
        }} />
      </div>

      {(() => {
        const rounds = pool.rounds ?? 1;
        const currentRound = pool.current_round ?? 1;
        const numPlayers = pool.num_players ?? 1;
        const draftComplete = currentRound > rounds;
        const isMultiRound = rounds > 1;

        const myTeamsThisRound = myTeam
          ? pool.teams.filter(t =>
              t.assigned_to &&
              myTeam.assigned_to &&
              t.assigned_to.toLowerCase() === myTeam.assigned_to.toLowerCase() &&
              t.round === currentRound
            )
          : [];
        const alreadyPickedThisRound = myTeamsThisRound.length > 0;

        if (draftComplete) {
          return (
            <div style={{
              marginBottom: 40, padding: '32px 40px',
              background: 'var(--ink)', color: 'var(--paper)', borderRadius: 12,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 16, position: 'relative', overflow: 'hidden',
            }}>
              <img src="/assets/trophy.svg" alt="" style={{
                position: 'absolute', right: '4%', top: '50%', transform: 'translateY(-50%)',
                width: 72, opacity: 0.6,
              }} />
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 30, textTransform: 'uppercase', letterSpacing: '-0.005em', lineHeight: 1, color: 'var(--gold)' }}>
                  DRAFT COMPLETE
                </div>
                <p style={{ margin: '10px 0 0', color: 'rgba(255,255,255,0.85)', fontSize: 15, maxWidth: 500 }}>
                  All {rounds} round{rounds === 1 ? '' : 's'} drawn. Now we watch the tournament unfold.
                </p>
              </div>
            </div>
          );
        }

        if (alreadyPickedThisRound && isMultiRound) {
          return (
            <div style={{
              marginBottom: 40, padding: '32px 40px',
              background: 'var(--paper-warm)', borderRadius: 12,
              border: '1.5px solid var(--hairline-strong)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 16,
            }}>
              <div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, textTransform: 'uppercase', letterSpacing: '-0.005em', color: 'var(--ink)' }}>
                  YOU'RE LOCKED IN FOR ROUND {currentRound}
                </div>
                <p style={{ margin: '10px 0 0', color: 'var(--ink-soft)', fontSize: 14, maxWidth: 540 }}>
                  Waiting on the rest of the room. Round {currentRound + 1 > rounds ? 'closes' : `${currentRound + 1} unlocks`} once everyone has drawn.
                </p>
              </div>
            </div>
          );
        }

        if (!myTeam || (isMultiRound && !alreadyPickedThisRound)) {
          return (
            <div style={{
              marginBottom: 40, padding: '32px 40px',
              background: 'var(--blue)', color: 'var(--paper)', borderRadius: 12,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 16, position: 'relative', overflow: 'hidden',
            }}>
              <img src="/assets/ball.svg" alt="" style={{
                position: 'absolute', right: '-12%', top: '50%', transform: 'translateY(-50%)',
                width: 280, opacity: 0.22,
              }} />
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 30, textTransform: 'uppercase', letterSpacing: '-0.005em', lineHeight: 1 }}>
                  {isMultiRound ? `ROUND ${currentRound} IS OPEN` : 'CLAIM YOUR TEAM'}
                </div>
                <p style={{ margin: '10px 0 0', color: 'rgba(255,255,255,0.85)', fontSize: 15, maxWidth: 500 }}>
                  {isMultiRound
                    ? `Draw your Round ${currentRound} team. Round ${currentRound + 1 > rounds ? `closes the draft` : `unlocks once everyone has drawn`}.`
                    : `${numPlayers === 1 ? 'You get' : 'Each player gets'} one random team. One shot, no re-rolls.`}
                </p>
              </div>
              <Button variant="onInk" onClick={onPickTeam} style={{ position: 'relative', zIndex: 1 }}>
                {isMultiRound ? `DRAW ROUND ${currentRound}` : 'DRAW MY TEAM'} <Icon name="arrow-right" size={16} />
              </Button>
            </div>
          );
        }

        return null;
      })()}

      <DashboardTabBar tab={tab} setTab={setTab} />

      {renderTabContent(onRefresh)}
    </section>
  );
};
